import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import { createEmptyPackage } from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';

const BATCH_MAX = 100;

const selectOptionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

/**
 * 批次新增包裝：共用數量／儲位／備註；單位繼承 line（不送 package unit）。
 */
const BatchAddPackagesDialog = ({
  open,
  onOpenChange,
  onConfirm,
  warehouseId,
  locationOptions = [],
  locationOptionsLoading = false,
}) => {
  const locationDisabled = !warehouseId;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      count: '1',
      locationId: '',
      qty: '1',
      remark: '',
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    reset({
      count: '1',
      locationId: '',
      qty: '1',
      remark: '',
    });
  }, [open, reset]);

  function handleConfirm(values) {
    const raw = Math.floor(Number(values.count));
    if (!Number.isFinite(raw) || raw < 1) {
      return;
    }
    const count = Math.min(BATCH_MAX, raw);
    const template = {
      ...createEmptyPackage(),
      locationId: values.locationId || '',
      qty: values.qty === '' || values.qty == null ? '' : values.qty,
      remark: values.remark || '',
    };
    onConfirm(Array.from({ length: count }, () => ({ ...template })));
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="批次新增包裝"
      description={`輸入包裝數（1–${BATCH_MAX}）與共用欄位，確認後一次新增。`}
      headerVariant="primary"
      onCancel={handleCancel}
      onConfirm={handleSubmit(handleConfirm)}
      confirmText="確認新增"
    >
      <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FloatingInput
          id="count"
          label="包裝數"
          type="number"
          register={register}
          errors={errors}
        />
        <FloatingInput
          id="qty"
          label="數量"
          type="number"
          register={register}
          errors={errors}
        />
        <div className="sm:col-span-2">
          <FloatingSelect
            id="locationId"
            label="儲位"
            tip="需要先在『單據資訊』選擇『倉別』，才可以選擇儲位"
            options={locationOptions}
            control={control}
            errors={errors}
            allowEmpty
            isClearable
            isDisabled={locationDisabled || locationOptionsLoading}
            noOptionsMessage={locationDisabled ? '請先選擇倉別' : '此倉別尚無儲位'}
          />
        </div>
        <div className="sm:col-span-2">
          <FloatingInput
            id="remark"
            label="包裝備註"
            type="text"
            register={register}
            errors={errors}
          />
        </div>
      </div>
    </AppDialog>
  );
};

BatchAddPackagesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  warehouseId: PropTypes.string,
  locationOptions: PropTypes.arrayOf(selectOptionShape),
  locationOptionsLoading: PropTypes.bool,
};

export default BatchAddPackagesDialog;
